import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser, apiRequest, API_ENDPOINTS, getAuthToken, clearAuthData } from '../../api/config';
import { useTheme } from './ThemeContext';
import { getTranslation } from './translations';

const Sidebar = ({ collapsed, mobileOpen, onClose, currentUser, onLogout, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useTheme();
  const t = getTranslation(language);
  
  const [userDetails, setUserDetails] = useState({
    email: currentUser?.email || '',
    name: currentUser?.name || '',
    balance: currentUser?.balance || 0,
    role: currentUser?.role || '',
    remainingCalls: currentUser?.remainingCalls || 0,
    totalCalls: currentUser?.totalCalls || 0,
    isMember: false
  });
  
  const lastFetchedEmail = useRef(null);
  
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (currentUser && currentUser.email !== lastFetchedEmail.current) {
        lastFetchedEmail.current = currentUser.email;
        try {
          const response = await apiRequest(
            API_ENDPOINTS.USER.PROFILE,
            {},
            true,
            'GET'
          );
          if (response.status === 'success' && response.data) {
            setUserDetails({
              email: response.data.email || currentUser.email,
              name: response.data.name || currentUser.email.split('@')[0],
              balance: response.data.balance || 0,
              role: response.data.role || currentUser.role,
              remainingCalls: response.data.remainingCalls || 0,
              totalCalls: response.data.totalCalls || 0,
              isMember: response.data.isMember || false
            });
          }
        } catch (error) {
          console.error('获取用户详情失败:', error);
        }
      }
    };
    fetchUserDetails();
  }, [currentUser]);

  const isAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'superadmin');
  const isActive = (path) => location.pathname === path;
  const hasPrefix = (prefix) => location.pathname.startsWith(prefix);

  const handleMenuItemClick = () => {
    if (window.innerWidth < 768 && onClose) {
      onClose();
    }
  };

  const handleLogoutClick = async (e) => {
    e.preventDefault();
    if (onLogout) onLogout();
  };

  const getUserInitials = () => {
    if (userDetails.name) return userDetails.name.charAt(0).toUpperCase();
    if (userDetails.email) return userDetails.email.charAt(0).toUpperCase();
    return '?';
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="brand">
        <div 
          className="brand-logo" 
          onClick={onToggle}
          style={{ cursor: 'pointer' }}
          title={collapsed ? t('expandMenu') : t('collapseMenu')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>
        <span className="brand-name" onClick={() => window.location.href = '/'} style={{cursor: 'pointer'}}>{language === 'zh' ? '奇迹妙审' : 'MagicAudit'}</span>
        {!collapsed && (
          <button className="collapse-btn" onClick={onToggle}>
            ◀
          </button>
        )}
      </div>

      <div className="nav-scroll">
        {!isAdmin && (
          <>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">📊</span><span className="nav-text">{t('dashboard')}</span>
            </div>

            {/* 斗篷功能菜单 */}
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/cloak/list'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: hasPrefix('/dashboard/cloak/list') ? 'var(--accent)' : 'var(--t2)', background: hasPrefix('/dashboard/cloak/list') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">🔗</span><span className="nav-text">{t('cloakList')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/cloak/logs'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/cloak/logs') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/cloak/logs') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">📋</span><span className="nav-text">{t('cloakLogs')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/cloak/plans'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/cloak/plans') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/cloak/plans') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">📦</span><span className="nav-text">{t('myPlans')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/cloak/pricing'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/cloak/pricing') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/cloak/pricing') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">🛒</span><span className="nav-text">{t('planPurchase')}</span>
            </div>
          </>
        )}

        {isAdmin && (
          <>
            <div className="group-label">{t('adminPanel')}</div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/admin/dashboard'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/admin/dashboard') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/admin/dashboard') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">📊</span><span className="nav-text">{t('adminDashboard')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/admin/users'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/admin/users') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/admin/users') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">👥</span><span className="nav-text">{t('userManagement')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/admin/payments'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/admin/payments') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/admin/payments') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">💳</span><span className="nav-text">{t('paymentReview')}</span>
            </div>
            <div
              className="nav-item"
              onClick={() => { navigate('/dashboard/admin/plans'); handleMenuItemClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 16px', color: isActive('/dashboard/admin/plans') ? 'var(--accent)' : 'var(--t2)', background: isActive('/dashboard/admin/plans') ? 'rgba(74,158,255,.13)' : 'transparent', cursor: 'pointer' }}
            >
              <span className="nav-icon">🛒</span><span className="nav-text">{t('planManagement')}</span>
            </div>
          </>
        )}
      </div>

      <div style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
        <button 
          onClick={handleLogoutClick}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '9px', 
            padding: '9px 16px', 
            width: '100%', 
            color: 'var(--red)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          <span className="nav-icon">🚪</span>
          <span className="nav-text">{t('logout')}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
